import { ActionGlyph } from '../../components/ActionGlyph'
import { ArtistNameplate } from '../../components/ArtistNameplate'
import { PresenterLockup } from '../../components/PresenterLockup'
import { TourMark } from '../../components/TourMark'
import { publicUrl } from '../../routing/basePath'
import { useAppActions, useAppState } from '../../state/AppProvider'
import { EmptyState, SectionHeading } from './FanPageChrome'
import {
  citySlug,
  formatLongDate,
  formatShortDate,
  showStatusLabel,
  showStatusTone,
  sortedShows,
  type Navigate,
} from './fanExperience'
import './fanExperience.css'

interface HomePageProps {
  navigate: Navigate
}

export function HomePage({ navigate }: HomePageProps) {
  const state = useAppState()
  const actions = useAppActions()
  const orderedShows = sortedShows(state.shows)
  const selectedShow = state.shows.find((show) => show.id === state.selectedShowId) ?? orderedShows[0]

  if (!selectedShow) {
    return (
      <div className="fan-page">
        <EmptyState
          actionLabel="Open tour"
          body="Tour stops will appear here when the route is available."
          onAction={() => navigate('/tour')}
          title="No stops yet"
        />
      </div>
    )
  }

  const ticket = state.tickets.find((item) => item.showId === selectedShow.id && item.status === 'connected')
  const checkedIn = state.checkIns.some((item) => item.showId === selectedShow.id)
  const stamped = state.passportStamps.some((item) => item.showId === selectedShow.id)
  const saved = state.savedShowIds.includes(selectedShow.id)
  const cityFeedPath = `/community/${citySlug(selectedShow)}/${state.preferences.defaultChannel}`
  const firstName = state.user?.displayName.trim().split(/\s+/)[0] || 'there'
  const unreadCount = state.notifications.reduce((count, item) => count + (item.readAt ? 0 : 1), 0)
  const savedStops = state.savedShowIds.length
  const nextShows = orderedShows
    .filter((show) => show.showStatus !== 'complete' && show.id !== selectedShow.id)
    .slice(0, 3)

  return (
    <div className="fan-page fan-home">
      <section className="fan-home-hero" aria-labelledby="home-title">
        <picture className="fan-home-hero__media">
          <source media="(min-width: 800px)" srcSet={publicUrl('assets/the-paradox-band-jump-1440.jpg')} />
          <img
            alt="The Paradox band jumping together outdoors"
            decoding="async"
            fetchPriority="high"
            height="540"
            src={publicUrl('assets/the-paradox-band-jump-960.jpg')}
            width="960"
          />
        </picture>
        <ArtistNameplate className="fan-home-hero__artist" context="Fan Field Kit" />
        <PresenterLockup />
        <div aria-hidden="true" className="fan-home-hero__noise" />
        <div className="fan-home-hero__topline">
          <p>{String(selectedShow.sequence).padStart(2, '0')} / {String(state.shows.length).padStart(2, '0')}</p>
          <button
            aria-label={`${unreadCount} unread notifications`}
            className="fan-notice-button"
            onClick={() => navigate('/notifications')}
            type="button"
          >
            <TourMark name="signal" />
            {unreadCount}
          </button>
        </div>
        <div className="fan-home-hero__copy">
          <p className="fan-eyebrow">Hey {firstName} · {selectedShow.city} is up</p>
          <h1
            className={`fan-home-hero__city ${selectedShow.city.length > 11 ? 'fan-home-hero__city--long' : ''}`}
            id="home-title"
          >
            {selectedShow.city}
          </h1>
          <p className="fan-home-hero__date">{formatLongDate(selectedShow.isoDate)}</p>
          <p className="fan-home-hero__venue">{selectedShow.venue}</p>
        </div>
        <button
          className="fan-hero-link"
          onClick={() => navigate(ticket ? `/shows/${selectedShow.id}` : '/tickets/connect')}
          type="button"
        >
          {ticket ? 'Show details' : 'Connect ticket'} <ActionGlyph direction="external" />
        </button>
      </section>

      <div className={`fan-status-rail fan-status-rail--${showStatusTone(selectedShow)}`}>
        <span>{showStatusLabel(selectedShow)}</span>
        <span>{selectedShow.doorsLabel}</span>
      </div>

      <section className="fan-home__access" aria-labelledby="access-heading">
        <SectionHeading eyebrow="Before the doors" id="access-heading" title="Your show" />
        <div className="fan-access-ledger">
          <button className="fan-ledger-row" onClick={() => navigate(ticket ? '/tickets' : '/tickets/connect')} type="button">
            <span className={`fan-ledger-row__state ${ticket ? 'is-complete' : ''}`} aria-hidden="true">
              <TourMark name={ticket ? 'check' : 'ticket'} />
            </span>
            <span>
              <strong>{ticket ? 'Ticket connected' : 'Connect your ticket'}</strong>
              <small>{ticket ? `Section ${ticket.section} · Row ${ticket.row}` : 'Add your show access to this account'}</small>
            </span>
            <ActionGlyph />
          </button>
          <button
            className="fan-ledger-row"
            disabled={!ticket && !checkedIn}
            onClick={() => navigate(`/check-in/${selectedShow.id}`)}
            type="button"
          >
            <span className={`fan-ledger-row__state ${checkedIn ? 'is-complete' : ''}`} aria-hidden="true">
              <TourMark name={checkedIn ? 'check' : 'flash'} />
            </span>
            <span>
              <strong>{checkedIn ? 'Checked in' : 'Show check-in'}</strong>
              <small>{checkedIn ? 'Your stop is recorded' : ticket ? 'See availability for this stop' : 'Connect a ticket first'}</small>
            </span>
            <ActionGlyph />
          </button>
          <button className="fan-ledger-row" onClick={() => navigate('/passport')} type="button">
            <span className={`fan-ledger-row__state ${stamped ? 'is-complete' : ''}`} aria-hidden="true">
              <TourMark name={stamped ? 'check' : 'passport'} />
            </span>
            <span>
              <strong>{stamped ? 'Passport stamped' : 'Tour passport'}</strong>
              <small>{state.passportStamps.length} of {state.shows.length} stops recorded</small>
            </span>
            <ActionGlyph />
          </button>
        </div>
      </section>

      <section className="fan-home__signal" aria-labelledby="signal-heading">
        <SectionHeading
          eyebrow="City update"
          id="signal-heading"
          title={selectedShow.city}
          trailing={<button className="fan-text-button" onClick={() => navigate(cityFeedPath)} type="button">Open Community <ActionGlyph /></button>}
        />
        <button className="fan-signal-note" onClick={() => navigate(cityFeedPath)} type="button">
          <span className="fan-signal-note__stamp">UPDATE</span>
          <span>
            <strong>{selectedShow.headline}</strong>
            <small>{selectedShow.update}</small>
          </span>
        </button>
      </section>

      <section className="fan-home__route" aria-labelledby="route-heading">
        <SectionHeading
          count={savedStops}
          eyebrow="The route"
          id="route-heading"
          title="Coming up"
          trailing={<button className="fan-text-button" onClick={() => navigate('/tour')} type="button">All stops <ActionGlyph /></button>}
        />
        <div className="fan-route-list">
          {nextShows.length ? nextShows.map((show) => {
            const isSaved = state.savedShowIds.includes(show.id)
            return (
              <article className="fan-route-row" key={show.id}>
                <button
                  className="fan-route-row__main"
                  onClick={() => {
                    actions.selectShow(show.id)
                    navigate(`/shows/${show.id}`)
                  }}
                  type="button"
                >
                  <span className="fan-route-row__date">{formatShortDate(show.isoDate)}</span>
                  <span>
                    <strong>{show.city}</strong>
                    <small>{show.venue}</small>
                  </span>
                </button>
                <button
                  aria-label={`${isSaved ? 'Remove' : 'Save'} ${show.city}`}
                  aria-pressed={isSaved}
                  className="fan-save-button"
                  onClick={() => actions.toggleSavedShow(show.id)}
                  type="button"
                >
                  <TourMark name={isSaved ? 'check' : 'route'} />
                </button>
              </article>
            )
          }) : (
            <p className="fan-muted">No more upcoming stops.</p>
          )}
        </div>
      </section>

      <div className="fan-home__finish">
        <button
          aria-pressed={saved}
          className={`fan-button ${saved ? 'fan-button--light' : 'fan-button--acid'}`}
          onClick={() => actions.toggleSavedShow(selectedShow.id)}
          type="button"
        >
          {saved ? 'Saved to your route' : 'Save this stop'}
        </button>
        <button className="fan-button fan-button--outline" onClick={() => navigate('/itinerary')} type="button">
          Plan show day
        </button>
        <button className="fan-button fan-button--acid fan-tour-desk-link" onClick={() => navigate('/content')} type="button">
          Open Tour Desk
        </button>
      </div>
    </div>
  )
}
